import { Extension, InputRule } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';

// Industry-standard screenplay elements as a paragraph attribute.
//
// Each script paragraph carries `scriptType` (scene | action | character |
// parenthetical | dialogue | transition), rendered as BOTH a class
// (script-scene, …) and a data attribute. The class drives layout —
// on screen and in the PDF/DOCX exports (xhtml2pdf has no attribute
// selectors, classes only); the data attribute is what parseHTML trusts
// when loading a saved document.
//
// Keyboard flow (Final-Draft-style):
//   Tab / Shift+Tab  cycle the current line's element type
//   Enter            next logical element (character → dialogue,
//                    dialogue → character, parenthetical → dialogue,
//                    scene → action, transition → scene)
//   Enter on empty   drops back to action (escape hatch)
//   Typing INT. / EXT. at line start auto-converts to a scene heading.

export type ScriptType =
  | 'scene'
  | 'action'
  | 'character'
  | 'parenthetical'
  | 'dialogue'
  | 'transition';

export const SCRIPT_TYPES: ScriptType[] = [
  'scene',
  'action',
  'character',
  'dialogue',
  'parenthetical',
  'transition',
];

// The names the writer sees for these elements are not here: this is a plain
// module and cannot reach the language pack. FormattingToolbar builds the
// label for each type from `t.editor.script*` instead.

// What Enter should create after each element
const ENTER_FLOW: Record<ScriptType, ScriptType> = {
  scene: 'action',
  action: 'action',
  character: 'dialogue',
  dialogue: 'character',
  parenthetical: 'dialogue',
  transition: 'scene',
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    screenplay: {
      setScriptType: (type: ScriptType | null) => ReturnType;
    };
  }
}

const currentScriptParagraph = (state: any): { node: any; pos: number } | null => {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'paragraph') {
      return { node, pos: $from.before(depth) };
    }
  }
  return null;
};

export const Screenplay = Extension.create({
  name: 'screenplay',
  // Run our Tab/Enter handlers before other extensions' defaults
  priority: 1000,

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          scriptType: {
            default: null,
            parseHTML: (element) =>
              (element.getAttribute('data-script') as ScriptType) || null,
            renderHTML: (attributes) => {
              if (!attributes.scriptType) return {};
              return {
                'data-script': attributes.scriptType,
                class: `script-${attributes.scriptType}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setScriptType:
        (type) =>
        ({ tr, state, dispatch }) => {
          const para = currentScriptParagraph(state);
          if (!para) return false;
          if (dispatch) {
            tr.setNodeMarkup(para.pos, undefined, {
              ...para.node.attrs,
              scriptType: type,
            });
            dispatch(tr);
          }
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    const cycle = (direction: 1 | -1) => () => {
      const { state } = this.editor;
      const para = currentScriptParagraph(state);
      const current: ScriptType | null = para?.node.attrs.scriptType || null;
      if (!para || !current) return false; // not a script line — let Tab fall through
      const index = SCRIPT_TYPES.indexOf(current);
      const next =
        SCRIPT_TYPES[(index + direction + SCRIPT_TYPES.length) % SCRIPT_TYPES.length];
      return this.editor.commands.setScriptType(next);
    };

    return {
      Tab: cycle(1),
      'Shift-Tab': cycle(-1),

      Enter: () => {
        const { state } = this.editor;
        const para = currentScriptParagraph(state);
        const current: ScriptType | null = para?.node.attrs.scriptType || null;
        if (!para || !current) return false;

        // Empty script line: Enter demotes it to action instead of
        // stacking empty elements (mirrors Final Draft)
        if (para.node.content.size === 0 && current !== 'action') {
          return this.editor.commands.setScriptType('action');
        }

        const nextType = ENTER_FLOW[current];
        return this.editor
          .chain()
          .splitBlock()
          .command(({ tr, state: s, dispatch }) => {
            const after = currentScriptParagraph(s);
            if (after && dispatch) {
              tr.setNodeMarkup(after.pos, undefined, {
                ...after.node.attrs,
                scriptType: nextType,
              });
              dispatch(tr);
            }
            return true;
          })
          .run();
      },
    };
  },

  addInputRules() {
    // "INT." / "EXT." / "INT./EXT." / "I/E" at the start of a plain line
    // turns it into a scene heading — the classic screenwriting on-ramp.
    return [
      new InputRule({
        find: /^(INT\.|EXT\.|INT\.\/EXT\.|I\/E)\s$/i,
        handler: ({ state, range, match }) => {
          const para = currentScriptParagraph(state);
          if (!para || para.node.attrs.scriptType) return;
          const { tr } = state;
          tr.setNodeMarkup(para.pos, undefined, {
            ...para.node.attrs,
            scriptType: 'scene',
          });
          // Keep the typed prefix (uppercased by CSS); restore the cursor
          tr.insertText(`${match[1].toUpperCase()} `, range.from, range.to);
          tr.setSelection(
            TextSelection.create(tr.doc, range.from + match[1].length + 1)
          );
        },
      }),
    ];
  },
});
